import { useCallback, useEffect, useRef, useState } from 'react'
import { Menu } from '@mantine/core'
import type { BuildPartDto } from '../../app/models/build'
import { getPartSvg } from './partSvgs'

interface Props {
  buildPart: BuildPartDto;
  position: { x: number; y: number }; // pixels relative to overlay container
  size: { w: number; h: number }; // pixels — per-category render size
  isOwner: boolean;
  isActiveZone: boolean;
  onDragEnd: (x: number, y: number) => void;
  onResetPosition: () => void;
}

export default function PartOverlayItem ({
  buildPart,
  position,
  size,
  isOwner,
  isActiveZone,
  onDragEnd,
  onResetPosition
}: Props) {
  const SvgComponent = getPartSvg(buildPart.part?.category)

  const [dragging, setDragging] = useState(false)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const [menuOpen, setMenuOpen] = useState(false)
  const dragStart = useRef<{ mx: number; my: number } | null>(null)
  const longPressTimer = useRef<ReturnType<typeof setTimeout>>()
  const hasDragged = useRef(false)

  // ── Drag (mouse + touch, no library) ──────────────────────────────────────
  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isOwner) return
    e.stopPropagation()

    hasDragged.current = false
    dragStart.current = { mx: e.clientX, my: e.clientY }

    // Long press → context menu
    longPressTimer.current = setTimeout(() => {
      if (!hasDragged.current) setMenuOpen(true)
    }, 500)

    setDragging(true)
    setOffset({ x: 0, y: 0 });
    (e.target as HTMLElement).setPointerCapture(e.pointerId)
  }, [isOwner])

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragStart.current) return
    const dx = e.clientX - dragStart.current.mx
    const dy = e.clientY - dragStart.current.my
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
      hasDragged.current = true
      clearTimeout(longPressTimer.current)
    }
    setOffset({ x: dx, y: dy })
  }, [])

  const onPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    clearTimeout(longPressTimer.current)
    if (!dragging) return
    setDragging(false)

    if (hasDragged.current && dragStart.current) {
      const dx = e.clientX - dragStart.current.mx
      const dy = e.clientY - dragStart.current.my
      onDragEnd(position.x + dx, position.y + dy)
    }

    setOffset({ x: 0, y: 0 })
    dragStart.current = null
  }, [dragging, position, onDragEnd])

  // Clean up long-press timer on unmount.
  useEffect(() => () => clearTimeout(longPressTimer.current), [])

  const accentColor = isActiveZone
    ? 'var(--gs-accent)'
    : 'var(--gs-part-stroke, #5A6A7A)'

  const transform = `translate(${position.x + offset.x - size.w / 2}px, ${position.y + offset.y - size.h / 2}px)`

  return (
    <Menu opened={menuOpen} onClose={() => setMenuOpen(false)} position="bottom-start">
      <Menu.Target>
        <div
          title={buildPart.part?.name ?? buildPart.part?.category ?? 'Part'}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: size.w,
            height: size.h,
            transform,
            cursor: isOwner ? (dragging ? 'grabbing' : 'grab') : 'default',
            pointerEvents: 'auto',
            userSelect: 'none',
            transition: dragging ? 'none' : 'transform 0.15s ease'
          }}
        >
          <div style={{
            width: '100%',
            height: '100%',
            borderRadius: 4,
            border: `1.5px solid ${accentColor}`,
            background: 'var(--gs-surface, #1C2128)',
            transform: isActiveZone ? 'scale(1.08)' : 'scale(1)',
            transition: 'transform 0.12s ease, border-color 0.12s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: isActiveZone
              ? `0 0 6px ${accentColor}`
              : '0 1px 4px rgba(0,0,0,0.4)'
          }}>
            <SvgComponent
              width={size.w - 6}
              height={size.h - 6}
              stroke={accentColor}
            />
          </div>
        </div>
      </Menu.Target>

      {isOwner && (
        <Menu.Dropdown
          sx={{ background: 'var(--gs-surface2)', border: '1px solid var(--gs-border)' }}
        >
          <Menu.Label sx={{ color: 'var(--gs-muted)', fontSize: 11 }}>
            {buildPart.part?.name ?? 'Part'}
          </Menu.Label>
          <Menu.Item
            onClick={() => { setMenuOpen(false); onResetPosition() }}
            sx={{ color: 'var(--gs-text)', fontSize: 13 }}
          >
            Reset to default position
          </Menu.Item>
        </Menu.Dropdown>
      )}
    </Menu>
  )
}
